import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, Params } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { routerNavigationAction, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { of, from, combineLatest, EMPTY } from 'rxjs';
import { KanbanStatus } from '@shared/models/kanban-status.enum';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
  take,
} from 'rxjs/operators';
import {
  createChildFailure,
  createChildRequested,
  createChildSuccess,
  enterWorkRoute,
  loadChildren,
  loadChildrenFailure,
  loadChildrenSuccess,
  loadDetails,
  loadDetailsFailure,
  loadDetailsSuccess,
  loadThreads,
  loadThreadsFailure,
  loadThreadsSuccess,
  createThread,
  createThreadFailure,
  createThreadSuccess,
  loadElement,
  loadElementFailure,
  loadElementSuccess,
  navigateUp,
  openChild,
  previewRequested,
  selectChild,
  setActiveTab,
  setExpandedNodeIds,
  setPartyTree,
  setFullPartyTree,
  setElementTree,
  setTreeLoading,
  setTreeError,
  resetWorkState,
  setQuickFilter,
  setTagFilter,
  setSearchScope,
  setSearchTerm,
  toggleNodeExpanded,
} from './work.actions';
import {
  selectActiveTabKey,
  selectCurrentElement,
  selectElementById,
  selectEntities,
  selectExpandedNodeIds,
  selectFullPartyTree,
  selectQuickFilter,
  selectTagFilter,
  selectSearchScope,
  selectSearchTerm,
  selectCurrentThreads,
  selectThreadsLoading,
  selectSelectedChildId,
} from './work.selectors';
import { WorkApiService } from '@core/services/api/work-api.service';
import { withMinimumLoadingTime } from '@shared/utility/loading.operators';
import { StateWorkType, WorkElement, WorkRouteQueryState, IEBWorkTreeNode } from './work.models';

@Injectable()
export class WorkEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly api = inject(WorkApiService);

  // Track which elements are being loaded by route navigation
  private routeElementIds = new Set<string>();
  private routeTreeElementIds = new Set<string>();
  private routeFullPartyTreeElementIds = new Set<string>();
  private pendingSyncRoute: { elementType: string; elementId: string } | null = null;

  readonly routeEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigationAction),
      map((action) => extractWorkRouteParams(action.payload.routerState)),
      filter((params): params is WorkRouteParams => !!params),
      withLatestFrom(this.store.select(selectCurrentElement)),
      filter(([{ elementId, elementType }]) => {
        if (
          this.pendingSyncRoute &&
          this.pendingSyncRoute.elementId === elementId &&
          this.pendingSyncRoute.elementType === elementType
        ) {
          this.pendingSyncRoute = null;
          return false;
        }
        return true;
      }),
      mergeMap(([{ elementId, elementType, query }, currentElement]) => {
        const isSameElement =
          currentElement?.id === elementId && currentElement?.type === elementType;

        const actions: Action[] = [
          enterWorkRoute({ elementId, elementType, query }),
        ];

        // Query-only route changes (e.g. tab switches) should not reload element/details.
        if (!isSameElement) {
          this.routeElementIds.add(elementId);
          this.routeTreeElementIds.add(elementId);
          this.routeFullPartyTreeElementIds.add(elementId);
          actions.push(loadElement({ id: elementId, elementType }));
          actions.push(loadDetails({ elementId, elementType }));
        }

        // If we navigate into a party root, reset the work store first to avoid stale state from previous navigations.
        if (!isSameElement && elementType === 'party') {
          actions.unshift(resetWorkState());
        }
        return from(actions);
      }),
    ),
  );

  // Ensure parent node is expanded so the newly created item is visible in the tree
  readonly expandParentOnCreateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createChildSuccess),
        withLatestFrom(this.store.select(selectExpandedNodeIds)),
        map(([{ parentId }, expanded]) => {
          const exists = (expanded ?? []).includes(parentId);
          if (exists) {
            return null;
          }
          const next = [...(expanded ?? []), parentId];
          return setExpandedNodeIds({ nodeIds: next });
        }),
        filter((action): action is ReturnType<typeof setExpandedNodeIds> => !!action),
      ),
    { dispatch: true },
  );

  // Select the newly created child to highlight it in the tree
  readonly selectNewChildOnCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createChildSuccess),
      map(({ child }) => selectChild({ childId: child.id, childType: child.type })),
    ),
  );

  // Load children for current element and parent (for tree siblings) after element is loaded via route
  readonly loadChildrenOnRouteEnter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadElementSuccess),
      filter(({ element }) => this.routeElementIds.has(element.id)),
      tap(({ element }) => this.routeElementIds.delete(element.id)),
      switchMap(({ element }) => {
        const actions = [loadChildren({ parentId: element.id, parentType: element.type })];
        
        // Also load parent's children to show siblings in tree (skip for party - it has no parent)
        if (element.parentId && element.type !== 'party') {
          // Determine parent type based on current element type
          let parentType: string;
          if (element.type === 'campaign') parentType = 'party';
          else if (element.type === 'action') parentType = 'campaign';
          else if (element.type === 'task') parentType = 'action';
          else parentType = 'party'; // fallback
          
          actions.push(loadChildren({ parentId: element.parentId, parentType }));
        }
        
        return from(actions);
      }),
    ),
  );

  readonly loadElement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadElement),
      switchMap(({ id, elementType }) =>
        this.store.select(selectElementById(id)).pipe(
          take(1),
          switchMap((existing) => {
            if (existing) {
              return of(loadElementSuccess({ element: existing }));
            }
            return this.api.fetchElement(id, elementType).pipe(
              map((element) => loadElementSuccess({ element })),
              catchError((error) => of(loadElementFailure({ id, error }))),
            );
          }),
        ),
      ),
    ),
  );

  readonly loadPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(previewRequested),
      switchMap(({ id, elementType }) =>
        this.api.fetchElement(id, elementType).pipe(
          withMinimumLoadingTime({ minimumMs: 200 }),
          map((element) => loadElementSuccess({ element })),
          catchError((error) => of(loadElementFailure({ id, error }))),
        ),
      ),
    ),
  );

  readonly loadChildren$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChildren),
      switchMap(({ parentId, parentType }) =>
        this.api.fetchChildren(parentId, parentType).pipe(
          withMinimumLoadingTime({ minimumMs: 220 }),
          map((children) => loadChildrenSuccess({ parentId, children })),
          catchError((error) => of(loadChildrenFailure({ parentId, error }))),
        ),
      ),
    ),
  );

  readonly loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDetails),
      switchMap(({ elementId, elementType }) =>
        this.api.fetchDetails(elementId, elementType).pipe(
          map((details) => loadDetailsSuccess({ elementId, details })),
          catchError((error) => of(loadDetailsFailure({ elementId, error }))),
        ),
      ),
    ),
  );

  readonly loadThreadsOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveTab),
      filter(({ tabKey }) => tabKey === 'threads'),
      withLatestFrom(
        this.store.select(selectCurrentElement),
        this.store.select(selectCurrentThreads),
        this.store.select(selectThreadsLoading),
      ),
      filter(([_, element]) => !!element && element.type === 'campaign'),
      filter(([, , threads, loading]) => !loading && (!threads || threads.length === 0)),
      map(([, element]) => loadThreads({ elementId: element!.id, elementType: element!.type })),
    ),
  );

  readonly loadThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThreads),
      switchMap(({ elementId, elementType }) =>
        this.api.fetchThreads(elementId, elementType).pipe(
          withMinimumLoadingTime({ minimumMs: 220 }),
          map((threads) => loadThreadsSuccess({ elementId, threads })),
          catchError((error) => of(loadThreadsFailure({ elementId, error }))),
        ),
      ),
    ),
  );

  readonly createThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createThread),
      switchMap(({ elementId, elementType, title, message }) =>
        this.api.createThread(elementId, elementType, { title, message }).pipe(
          map((thread) => createThreadSuccess({ elementId, thread })),
          catchError((error) => of(createThreadFailure({ elementId, error }))),
        ),
      ),
    ),
  );

  readonly setDefaultTabOnDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDetailsSuccess),
      withLatestFrom(this.store.select(selectActiveTabKey)),
      map(([{ details }, activeTabKey]) => {
        const tabKeys =
          details?.tabs
            ?.map((tab) => tab?.key)
            .filter((key): key is string => typeof key === 'string' && key.length > 0) ?? [];

        if (tabKeys.length === 0) {
          return null;
        }

        const hasValidActiveTab = !!activeTabKey && tabKeys.includes(activeTabKey);
        if (hasValidActiveTab) {
          return null;
        }

        return setActiveTab({ tabKey: tabKeys[0] });
      }),
      filter((action): action is ReturnType<typeof setActiveTab> => !!action),
    ),
  );

  readonly loadThreadsIfActiveOnDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDetailsSuccess),
      withLatestFrom(
        this.store.select(selectActiveTabKey),
        this.store.select(selectCurrentElement),
        this.store.select(selectCurrentThreads),
        this.store.select(selectThreadsLoading),
      ),
      filter(([, activeTabKey]) => activeTabKey === 'threads'),
      filter(([, , element]) => !!element && element.type === 'campaign'),
      filter(([, , , threads, loading]) => !loading && (!threads || threads.length === 0)),
      map(([, , element]) => loadThreads({ elementId: element!.id, elementType: element!.type })),
    ),
  );

  readonly fetchTreeOnRouteEnter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadElementSuccess),
        filter(({ element }) => this.routeTreeElementIds.has(element.id)),
        tap(({ element }) => this.routeTreeElementIds.delete(element.id)),
        switchMap(({ element }) => {
          // If the element is a party, fetch the full party tree
          if (element.type === 'party') {
            return of(setTreeLoading({ parentId: element.id, loading: true })).pipe(
              switchMap(() =>
                this.api.getPartyWorkTree(element.id).pipe(
                  tap((tree) =>
                    console.log('[Work] Full party tree', {
                      partyId: element.id,
                      tree,
                    }),
                  ),
                  mergeMap((tree) => [
                    setTreeLoading({ parentId: element.id, loading: false }),
                    setTreeError({ parentId: element.id, error: null }),
                    setPartyTree({ partyId: element.id, tree }),
                  ]),
                  catchError((error) => {
                    console.error('[Work] Failed to load party tree', {
                      partyId: element.id,
                      error,
                    });
                    return of(
                      setTreeLoading({ parentId: element.id, loading: false }),
                      setTreeError({ parentId: element.id, error }),
                    );
                  }),
                ),
              ),
            );
          } else if (element.type === 'task') {
            // Skip tree loading for tasks - they are leaf nodes and should never be tree roots
            // The fullPartyTree (loaded by fetchPartyTreeOnRouteEnter$) provides navigation context
            console.log('[Work] Skipping element tree for task (leaf node)', {
              taskId: element.id,
            });
            return EMPTY;
          } else {
            // For campaigns and actions, fetch the element tree
            return of(setTreeLoading({ parentId: element.id, loading: true })).pipe(
              switchMap(() =>
                this.api.getElementWorkTree(element.id, element.type).pipe(
                  tap((tree) =>
                    console.log('[Work] Element subtree loaded on route enter', {
                      elementId: element.id,
                      elementType: element.type,
                      tree,
                    }),
                  ),
                  mergeMap((tree) => [
                    setTreeLoading({ parentId: element.id, loading: false }),
                    setTreeError({ parentId: element.id, error: null }),
                    setElementTree({ elementId: element.id, elementType: element.type, tree }),
                  ]),
                  catchError((error) => {
                    console.error('[Work] Failed to load element subtree on route enter', {
                      elementId: element.id,
                      elementType: element.type,
                      error,
                    });
                    return of(
                      setTreeLoading({ parentId: element.id, loading: false }),
                      setTreeError({ parentId: element.id, error }),
                    );
                  }),
                ),
              ),
            );
          }
        }),
      ),
    { dispatch: true },
  );

  readonly fetchPartyTreeOnRouteEnter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadElementSuccess),
        filter(({ element }) => this.routeFullPartyTreeElementIds.has(element.id)),
        tap(({ element }) => this.routeFullPartyTreeElementIds.delete(element.id)),
        switchMap(({ element }) =>
          this.store.select(selectEntities).pipe(
            take(1),
            map((entities) => this.resolvePartyIdForElement(element, entities)),
            filter((partyId): partyId is string => !!partyId),
            filter((partyId) => element.id !== partyId), // Skip if it's a party element (handled above)
            switchMap((partyId) =>
              this.api.getPartyWorkTree(partyId).pipe(
                tap((tree) =>
                  console.log('[Work] Full party tree loaded for navigation', {
                    partyId,
                    elementId: element.id,
                    elementType: element.type,
                    tree,
                  }),
                ),
                // Use setFullPartyTree - only updates fullPartyTree for navigation
                // Does NOT overwrite partyTree (display subtree)
                map((tree) => setFullPartyTree({ partyId, tree })),
                catchError((error) => {
                  console.error('[Work] Failed to load full party tree', {
                    partyId,
                    error,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      ),
    { dispatch: true },
  );

  private resolvePartyIdForElement(
    element: WorkElement,
    entities: StateWorkType['entities'],
  ): string | null {
    if (element.type === 'party') {
      return element.id;
    }
    if (element.type === 'campaign') {
      return element.parentId ?? null;
    }
    if (element.type === 'action') {
      const parent = element.parentId ? entities.elementsById[element.parentId] : null;
      return parent?.parentId ?? null;
    }
    if (element.type === 'task') {
      const actionParent = element.parentId ? entities.elementsById[element.parentId] : null;
      const campaignParent = actionParent?.parentId
        ? entities.elementsById[actionParent.parentId]
        : null;
      return campaignParent?.parentId ?? null;
    }
    return null;
  }

  readonly createChild$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createChildRequested),
      switchMap(({ parentId, elementType, payload }) =>
        this.api.createChild(parentId, elementType, payload).pipe(
          map((child) => createChildSuccess({ parentId, child })),
          catchError((error) => of(createChildFailure({ parentId, error }))),
        ),
      ),
    ),
  );

  // After successfully creating a child, refresh the tree for the current route element (party or element)
  // to ensure the new item appears in the tree without requiring navigation.
  readonly refreshTreeOnCreateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createChildSuccess),
        switchMap(({ parentId }) =>
          this.store.select(selectElementById(parentId)).pipe(
            take(1),
            switchMap((parent) => {
              if (!parent) {
                return EMPTY;
              }
              const startLoading = of(setTreeLoading({ parentId, loading: true }));
              const tree$ = parent.type === 'party'
                ? this.api.getPartyWorkTree(parentId).pipe(
                    withMinimumLoadingTime({ minimumMs: 220 }),
                    mergeMap((tree) => [
                      setTreeLoading({ parentId, loading: false }),
                      setTreeError({ parentId, error: null }),
                      setPartyTree({ partyId: parentId, tree }),
                    ]),
                    catchError((error) =>
                      of(
                        setTreeLoading({ parentId, loading: false }),
                        setTreeError({ parentId, error }),
                      ),
                    ),
                  )
                : this.api.getElementWorkTree(parentId, parent.type).pipe(
                    withMinimumLoadingTime({ minimumMs: 220 }),
                    mergeMap((tree) => [
                      setTreeLoading({ parentId, loading: false }),
                      setTreeError({ parentId, error: null }),
                      setElementTree({ elementId: parentId, elementType: parent.type, tree }),
                    ]),
                    catchError((error) =>
                      of(
                        setTreeLoading({ parentId, loading: false }),
                        setTreeError({ parentId, error }),
                      ),
                    ),
                  );
              return startLoading.pipe(switchMap(() => tree$));
            }),
          ),
        ),
      ),
    { dispatch: true },
  );

  readonly refreshFullPartyTreeOnCreateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createChildSuccess),
      withLatestFrom(this.store.select(selectEntities)),
      switchMap(([{ parentId }, entities]) =>
        this.store.select(selectElementById(parentId)).pipe(
          take(1),
          switchMap((parent) => {
            if (!parent) {
              return EMPTY;
            }

            const partyId = this.resolvePartyIdForElement(parent, entities);
            if (!partyId || partyId === parentId) {
              return EMPTY;
            }

            return this.api.getPartyWorkTree(partyId).pipe(
              map((tree) => setFullPartyTree({ partyId, tree })),
              catchError((error) => {
                console.error('[Work] Failed to refresh full party tree after create', {
                  partyId,
                  parentId,
                  error,
                });
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    ),
  );

  readonly openChildNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openChild),
        withLatestFrom(
          combineLatest([
            this.store.select(selectQuickFilter),
            this.store.select(selectTagFilter),
            this.store.select(selectSearchTerm),
            this.store.select(selectSearchScope),
            this.store.select(selectActiveTabKey),
            this.store.select(selectExpandedNodeIds),
          ]),
        ),
        tap(([{ childId, childType }, [quickFilter, tagFilter, searchTerm, searchScope, activeTabKey, expandedNodeIds]]) => {
          const queryParams = buildQueryParams({
            statuses: quickFilter,
            tagIds: tagFilter,
            searchTerm,
            searchScope,
            activeTabKey,
            selectedChildId: null,
            expandedNodeIds,
          });
          this.router.navigate(['/work', childType, childId], {
            queryParams,
          });
        }),
      ),
    { dispatch: false },
  );

  readonly navigateUp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(navigateUp),
        withLatestFrom(
          combineLatest([
            this.store.select(selectCurrentElement),
            this.store.select(selectEntities),
            this.store.select(selectFullPartyTree),
            this.store.select(selectQuickFilter),
            this.store.select(selectTagFilter),
            this.store.select(selectSearchTerm),
            this.store.select(selectSearchScope),
            this.store.select(selectActiveTabKey),
            this.store.select(selectExpandedNodeIds),
          ]),
        ),
        tap(([, [currentElement, entities, fullPartyTree, quickFilter, tagFilter, searchTerm, searchScope, activeTabKey, expandedNodeIds]]) => {
          const parentFromTree = this.resolveParentFromTree(currentElement?.id ?? null, fullPartyTree);
          const parentId = parentFromTree?.id ?? currentElement?.parentId ?? null;
          
          // Determine parent type: from tree, from entities cache, or infer from current element type
          const parentType = parentFromTree?.type 
            ?? (parentId ? entities.elementsById[parentId]?.type : null)
            ?? this.inferParentType(currentElement?.type ?? null);

          if (!parentId || !parentType) {
            console.warn('[Work] Cannot navigate up - no parent found', {
              currentElement: currentElement?.id,
              currentType: currentElement?.type,
              parentId,
              parentType,
            });
            this.router.navigate(['/gantt']);
            return;
          }
          
          console.log('[Work] Navigating up', {
            from: { id: currentElement?.id, type: currentElement?.type },
            to: { id: parentId, type: parentType },
          });
          
          // Preserve existing expansion state (KISS principle)
          // Only ensure parent and current element are in expanded list for context
          const newExpandedIds = Array.from(
            new Set([
              ...(expandedNodeIds ?? []),  // Preserve user's expansion choices
              currentElement?.id ?? '',     // Show where they came from
              parentId,                     // Ensure parent is visible
            ].filter(Boolean))
          );
          
          const queryParams = buildQueryParams({
            statuses: quickFilter,
            tagIds: tagFilter,
            searchTerm,
            searchScope,
            activeTabKey,
            selectedChildId: null,
            expandedNodeIds: newExpandedIds,
          });
          
          // Navigate and let route enter effects handle data loading
          // This prevents race conditions and duplicate requests
          this.router.navigate(['/work', parentType, parentId], {
            queryParams,
          });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Infer parent element type from child element type.
   * Hierarchy: party → campaign → action → task
   */
  private inferParentType(childType: string | null): string | null {
    switch (childType) {
      case 'task': return 'action';
      case 'action': return 'campaign';
      case 'campaign': return 'party';
      default: return null;
    }
  }

  private resolveParentFromTree(
    currentId: string | null,
    tree: IEBWorkTreeNode | null,
  ): { id: string; type: string } | null {
    if (!currentId || !tree) {
      return null;
    }

    const stack: { node: IEBWorkTreeNode; parent: IEBWorkTreeNode | null }[] = [{ node: tree, parent: null }];
    while (stack.length) {
      const { node, parent } = stack.pop()!;
      if (node.id === currentId) {
        return parent ? { id: parent.id, type: parent.type } : null;
      }
      for (const child of node.children ?? []) {
        stack.push({ node: child, parent: node });
      }
    }
    return null;
  }

  /**
   * Find a node by ID in the tree structure.
   * Returns the node if found, null otherwise.
   */
  private findNodeInTree(nodeId: string, tree: IEBWorkTreeNode): IEBWorkTreeNode | null {
    if (tree.id === nodeId) {
      return tree;
    }

    const stack: IEBWorkTreeNode[] = [tree];
    while (stack.length) {
      const node = stack.pop()!;
      if (node.id === nodeId) {
        return node;
      }
      for (const child of node.children ?? []) {
        stack.push(child);
      }
    }
    return null;
  }

  readonly syncQueryParams$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          setQuickFilter,
          setTagFilter,
          setSearchTerm,
          setSearchScope,
          setActiveTab,
          selectChild,
          toggleNodeExpanded,
          setExpandedNodeIds,
        ),
        withLatestFrom(
          combineLatest([
            this.store.select(selectCurrentElement),
            this.store.select(selectQuickFilter),
            this.store.select(selectTagFilter),
            this.store.select(selectSearchTerm),
            this.store.select(selectSearchScope),
            this.store.select(selectActiveTabKey),
            this.store.select(selectSelectedChildId),
            this.store.select(selectExpandedNodeIds),
          ]),
        ),
        tap(([action, [currentElement, quickFilter, tagFilter, searchTerm, searchScope, activeTabKey, selectedChildId, expandedNodeIds]]) => {
          if (!currentElement) {
            return;
          }

          // For tab changes, use the new tab key directly from the action to avoid
          // transient stale selector values during the same dispatch tick.
          const effectiveActiveTabKey =
            action.type === setActiveTab.type
              ? (action as ReturnType<typeof setActiveTab>).tabKey
              : activeTabKey;

          const queryParams = buildQueryParams({
            statuses: quickFilter,
            tagIds: tagFilter,
            searchTerm,
            searchScope,
            activeTabKey: effectiveActiveTabKey,
            selectedChildId,
            expandedNodeIds,
          });
          this.pendingSyncRoute = {
            elementType: currentElement.type,
            elementId: currentElement.id,
          };
          void this.router
            .navigate(['/work', currentElement.type, currentElement.id], {
              queryParams,
              replaceUrl: true,
            })
            .finally(() => {
              this.pendingSyncRoute = null;
            });
        }),
      ),
    { dispatch: false },
  );
}

interface WorkRouteParams {
  elementId: string;
  elementType: string;
  query: WorkRouteQueryState;
}

function extractWorkRouteParams(
  routerState: SerializedRouterStateSnapshot,
): WorkRouteParams | null {
  const leaf = getDeepestChild(routerState.root);
  if (!leaf?.params || !leaf?.url?.length) {
    return null;
  }
  const elementId = leaf.params['elementId'];
  const elementType = leaf.params['elementType'];
  if (typeof elementId !== 'string' || typeof elementType !== 'string') {
    return null;
  }
  const query = normalizeQueryParams(leaf.queryParams ?? {});
  return { elementId, elementType, query };
}

function getDeepestChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let current = route;
  while (current.firstChild) {
    current = current.firstChild;
  }
  return current;
}

function normalizeQueryParams(query: Params): WorkRouteQueryState {
  const toArray = (value: string | string[] | undefined): string[] => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.flatMap((item) => (item ?? '').toString().split(',')).filter(Boolean);
    }
    return value
      .toString()
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const scope = query['scope'];
  const searchScope = scope === 'tab' ? 'tab' : 'tree';

  return {
    searchTerm: query['q'] ?? '',
    searchScope,
    statuses: toArray(query['status']).map((s) => s as KanbanStatus),
    tagIds: toArray(query['tags']),
    activeTabKey: query['tab'] ?? null,
    selectedChildId: query['selectedChild'] ?? null,
    expandedNodeIds: toArray(query['expanded']),
  };
}

function buildQueryParams(query: WorkRouteQueryState): Record<string, string | undefined> {
  return {
    q: query.searchTerm ?? '',
    scope: query.searchScope ?? 'tree',
    status: (query.statuses ?? []).join(','),
    tags: (query.tagIds ?? []).join(','),
    tab: query.activeTabKey ?? undefined,
    selectedChild: query.selectedChildId ?? undefined,
    expanded: (query.expandedNodeIds ?? []).join(','),
  };
}
